import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MapPin,
  Search,
  Crosshair,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  Info,
  Layers,
  Sparkles,
  Compass,
} from 'lucide-react';
import L from 'leaflet';
import {
  NIGERIAN_STATES,
  getLgasForState,
  isValidLgaForState,
} from '../../data/nigerianLocations';
import {
  searchAddressAutocomplete,
  reverseGeocodeCoordinates,
  getDeviceCurrentLocation,
  LocationSuggestion,
} from '../../lib/locationService';

export interface SelectedLocationData {
  state: string;
  lga: string;
  cityArea: string;
  address: string;
  formattedAddress: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  isLgaConfident: boolean;
  confidenceScore: number;
  locationConfirmed: boolean;
  lgaConfirmed: boolean;
  detectionMethod: string;
}

interface LocationMapInputProps {
  initialState?: string;
  initialLga?: string;
  initialAddress?: string;
  initialCityArea?: string;
  initialLatitude?: number;
  initialLongitude?: number;
  onLocationSelected: (locationData: SelectedLocationData) => void;
  required?: boolean;
}

// Fix Leaflet default icon asset paths in Vite bundles
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const LocationMapInput: React.FC<LocationMapInputProps> = ({
  initialState = 'Lagos',
  initialLga = 'Ikeja',
  initialAddress = '',
  initialCityArea = '',
  initialLatitude,
  initialLongitude,
  onLocationSelected,
  required = true,
}) => {
  // Input mode: 'map' (Geoapify + OpenStreetMap search) or 'manual' (State + LGA dropdowns)
  const [mode, setMode] = useState<'map' | 'manual'>('map');

  // Search input state
  const [searchQuery, setSearchQuery] = useState<string>(initialAddress);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

  // Resolved Location state
  const [selectedState, setSelectedState] = useState<string>(initialState || 'Lagos');
  const [selectedLga, setSelectedLga] = useState<string>(initialLga || 'Ikeja');
  const [cityArea, setCityArea] = useState<string>(initialCityArea || '');
  const [streetAddress, setStreetAddress] = useState<string>(initialAddress || '');
  const [formattedAddress, setFormattedAddress] = useState<string>(initialAddress || '');
  const [latitude, setLatitude] = useState<number | undefined>(initialLatitude);
  const [longitude, setLongitude] = useState<number | undefined>(initialLongitude);
  const [placeId, setPlaceId] = useState<string | undefined>();
  const [isLgaConfident, setIsLgaConfident] = useState<boolean>(true);
  const [confidenceScore, setConfidenceScore] = useState<number>(85);
  const [lgaConfirmed, setLgaConfirmed] = useState<boolean>(true);

  // Status & loading states
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  // Map & Leaflet DOM refs
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerInstanceRef = useRef<L.Marker | null>(null);
  const searchDebounceRef = useRef<any>(null);

  // Available LGAs for current selected state
  const availableLgas = selectedState ? getLgasForState(selectedState) : [];

  // Notify parent of location changes
  const notifyParent = useCallback(
    (overrides?: Partial<SelectedLocationData>) => {
      onLocationSelected({
        state: overrides?.state ?? selectedState,
        lga: overrides?.lga ?? selectedLga,
        cityArea: overrides?.cityArea ?? cityArea,
        address: overrides?.address ?? streetAddress,
        formattedAddress: overrides?.formattedAddress ?? formattedAddress,
        latitude: overrides?.latitude ?? latitude,
        longitude: overrides?.longitude ?? longitude,
        placeId: overrides?.placeId ?? placeId,
        isLgaConfident: overrides?.isLgaConfident ?? isLgaConfident,
        confidenceScore: overrides?.confidenceScore ?? confidenceScore,
        locationConfirmed: true,
        lgaConfirmed: overrides?.lgaConfirmed ?? lgaConfirmed,
        detectionMethod: overrides?.detectionMethod ?? (latitude ? 'geoapify_osm_pin' : 'manual_selection'),
      });
    },
    [
      selectedState,
      selectedLga,
      cityArea,
      streetAddress,
      formattedAddress,
      latitude,
      longitude,
      placeId,
      isLgaConfident,
      confidenceScore,
      lgaConfirmed,
      onLocationSelected,
    ]
  );

  // Initialize Leaflet Map
  useEffect(() => {
    if (mode !== 'map' || !mapContainerRef.current) return;

    // Destroy existing instance if container changed
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerInstanceRef.current = null;
    }

    const defaultLat = latitude || 6.5244;
    const defaultLng = longitude || 3.3792;
    const defaultZoom = latitude ? 14 : 11;

    try {
      const map = L.map(mapContainerRef.current, {
        center: [defaultLat, defaultLng],
        zoom: defaultZoom,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      // Marker setup
      const marker = L.marker([defaultLat, defaultLng], {
        icon: defaultIcon,
        draggable: true,
      }).addTo(map);

      // Handle marker drag end to reverse-geocode
      marker.on('dragend', async () => {
        const pos = marker.getLatLng();
        handleMapCoordinateUpdate(pos.lat, pos.lng);
      });

      // Handle map click to reposition marker
      map.on('click', async (e: L.LeafletMouseEvent) => {
        marker.setLatLng(e.latlng);
        handleMapCoordinateUpdate(e.latlng.lat, e.latlng.lng);
      });

      mapInstanceRef.current = map;
      markerInstanceRef.current = marker;

      // Ensure proper container size rendering
      setTimeout(() => {
        map.invalidateSize();
      }, 250);
    } catch (err) {
      console.warn('Map initialization note:', err);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerInstanceRef.current = null;
      }
    };
  }, [mode]);

  // Update map view when coordinates change
  const updateMapPosition = (lat: number, lng: number, zoom = 14) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], zoom);
      mapInstanceRef.current.invalidateSize();
    }
    if (markerInstanceRef.current) {
      markerInstanceRef.current.setLatLng([lat, lng]);
    }
  };

  // Handle coordinate updates from map interactions (drag/click)
  const handleMapCoordinateUpdate = async (lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);

    try {
      const result = await reverseGeocodeCoordinates(lat, lng);
      if (result) {
        const norm = result.resolvedNigerian;
        setSelectedState(norm.state);
        setSelectedLga(norm.lga);
        setCityArea(norm.cityArea);
        setStreetAddress(result.addressLine1 || result.formatted);
        setFormattedAddress(result.formatted);
        setSearchQuery(result.formatted);
        setIsLgaConfident(norm.isLgaConfident);
        setConfidenceScore(norm.confidenceScore);
        setLgaConfirmed(norm.isLgaConfident);

        notifyParent({
          state: norm.state,
          lga: norm.lga,
          cityArea: norm.cityArea,
          address: result.addressLine1 || result.formatted,
          formattedAddress: result.formatted,
          latitude: lat,
          longitude: lng,
          placeId: result.placeId,
          isLgaConfident: norm.isLgaConfident,
          confidenceScore: norm.confidenceScore,
          lgaConfirmed: norm.isLgaConfident,
          detectionMethod: 'osm_pin_reverse_geocode',
        });
      }
    } catch (err) {
      console.warn('Reverse geocode error:', err);
    }
  };

  // Autocomplete debounce handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    setGeoError(null);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (val.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await searchAddressAutocomplete(val, 6);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (err) {
        console.warn('Autocomplete search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 280);
  };

  // Select autocomplete suggestion
  const handleSelectSuggestion = (s: LocationSuggestion) => {
    setShowSuggestions(false);
    setSearchQuery(s.formatted);

    const norm = s.resolvedNigerian;
    setSelectedState(norm.state);
    setSelectedLga(norm.lga);
    setCityArea(norm.cityArea);
    setStreetAddress(s.addressLine1 || s.formatted);
    setFormattedAddress(s.formatted);
    setLatitude(s.lat);
    setLongitude(s.lon);
    setPlaceId(s.placeId);
    setIsLgaConfident(norm.isLgaConfident);
    setConfidenceScore(norm.confidenceScore);
    setLgaConfirmed(norm.isLgaConfident);

    updateMapPosition(s.lat, s.lon, 15);

    notifyParent({
      state: norm.state,
      lga: norm.lga,
      cityArea: norm.cityArea,
      address: s.addressLine1 || s.formatted,
      formattedAddress: s.formatted,
      latitude: s.lat,
      longitude: s.lon,
      placeId: s.placeId,
      isLgaConfident: norm.isLgaConfident,
      confidenceScore: norm.confidenceScore,
      lgaConfirmed: norm.isLgaConfident,
      detectionMethod: 'geoapify_autocomplete_selection',
    });
  };

  // Device GPS "Use My Current Location"
  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    setGeoError(null);

    try {
      const loc = await getDeviceCurrentLocation();
      setLatitude(loc.latitude);
      setLongitude(loc.longitude);

      updateMapPosition(loc.latitude, loc.longitude, 15);
      await handleMapCoordinateUpdate(loc.latitude, loc.longitude);
    } catch (err: any) {
      console.warn('GPS location request error:', err);
      setGeoError(err.message || 'Could not fetch device GPS. Please search your area manually.');
    } finally {
      setIsLocating(false);
    }
  };

  // Handle manual state dropdown change
  const handleManualStateChange = (newState: string) => {
    setSelectedState(newState);
    const lgas = getLgasForState(newState);
    const newLga = lgas[0] || '';
    setSelectedLga(newLga);
    setIsLgaConfident(true);
    setConfidenceScore(100);
    setLgaConfirmed(true);

    notifyParent({
      state: newState,
      lga: newLga,
      isLgaConfident: true,
      confidenceScore: 100,
      lgaConfirmed: true,
      detectionMethod: 'manual_dropdown',
    });
  };

  // Handle manual LGA dropdown change
  const handleManualLgaChange = (newLga: string) => {
    setSelectedLga(newLga);
    setIsLgaConfident(true);
    setConfidenceScore(100);
    setLgaConfirmed(true);

    notifyParent({
      lga: newLga,
      isLgaConfident: true,
      confidenceScore: 100,
      lgaConfirmed: true,
      detectionMethod: 'manual_dropdown',
    });
  };

  return (
    <div className="space-y-4">
      {/* Top Header & Mode Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-1">
        <div>
          <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider">
            Job Location (Nigeria) {required && <span className="text-emerald-700">*</span>}
          </label>
          <p className="text-[11px] text-slate-500">
            Search your landmark, estate or street. We match verified professionals in your LGA.
          </p>
        </div>

        <div className="flex items-center gap-1.5 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setMode(mode === 'map' ? 'manual' : 'map')}
            className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Layers className="w-3.5 h-3.5 text-slate-500" />
            <span>{mode === 'map' ? 'Switch to Manual State/LGA' : 'Use Map & Address Search'}</span>
          </button>
        </div>
      </div>

      {mode === 'map' ? (
        <div className="space-y-3">
          {/* Autocomplete Search Bar & GPS Locate Me Button */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                <input
                  type="text"
                  placeholder="e.g. Plot 18 Admiralty Way, Lekki or Shoprite Asaba..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (suggestions.length > 0) setShowSuggestions(true);
                  }}
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-300 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 text-xs sm:text-sm text-slate-900 bg-white transition-all shadow-xs"
                />
                {isSearching && (
                  <Loader2 className="w-4 h-4 text-emerald-600 animate-spin absolute right-3.5 top-3.5" />
                )}
              </div>

              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isLocating}
                className="px-3.5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 transition-colors shadow-xs cursor-pointer min-h-[44px]"
                title="Detect my current location via GPS"
              >
                {isLocating ? (
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                ) : (
                  <Crosshair className="w-4 h-4 text-emerald-400" />
                )}
                <span className="hidden sm:inline">Use Current Location</span>
              </button>
            </div>

            {/* Autocomplete Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-2xl border border-slate-200 shadow-xl z-50 overflow-hidden divide-y divide-slate-100 max-h-72 overflow-y-auto">
                <div className="px-3.5 py-2 bg-slate-50 text-[11px] font-semibold text-slate-500 flex items-center justify-between">
                  <span>Suggested Nigerian Locations</span>
                  <span className="text-[10px] text-slate-400">Powered by Geoapify & OSM</span>
                </div>
                {suggestions.map((s) => (
                  <button
                    key={s.placeId}
                    type="button"
                    onClick={() => handleSelectSuggestion(s)}
                    className="w-full px-3.5 py-2.5 text-left hover:bg-emerald-50/70 transition-colors flex items-start gap-2.5 text-xs text-slate-800 cursor-pointer"
                  >
                    <MapPin className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 truncate">
                        {s.addressLine1 || s.formatted}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {s.addressLine2 || `${s.resolvedNigerian.lga}, ${s.resolvedNigerian.state}`}
                      </p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 shrink-0">
                      {s.resolvedNigerian.state}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* GPS Error Notice */}
          {geoError && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">{geoError}</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  You can type your address directly in the search bar above or choose your State & LGA below.
                </p>
              </div>
            </div>
          )}

          {/* Interactive OpenStreetMap Container */}
          <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-xs relative">
            <div
              ref={mapContainerRef}
              className="w-full h-56 sm:h-64 bg-slate-100 z-10"
              style={{ minHeight: '220px' }}
            />

            {/* Map instruction overlay */}
            <div className="absolute bottom-2 left-2 right-2 sm:right-auto z-20 pointer-events-none">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/85 backdrop-blur-xs text-white text-[10px] sm:text-xs font-medium shadow-sm">
                <Compass className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                Click anywhere or drag pin to fine-tune location
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* Manual Cascading State & LGA Selectors */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Select Nigerian State
            </label>
            <div className="relative">
              <select
                value={selectedState}
                onChange={(e) => handleManualStateChange(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs sm:text-sm font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-1 focus:ring-emerald-500 appearance-none min-h-[44px]"
              >
                {NIGERIAN_STATES.map((st) => (
                  <option key={st.id} value={st.name}>
                    {st.name} {st.isFct ? '(Abuja)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Select Local Government Area (LGA)
            </label>
            <div className="relative">
              <select
                value={selectedLga}
                onChange={(e) => handleManualLgaChange(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs sm:text-sm font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-1 focus:ring-emerald-500 appearance-none min-h-[44px]"
              >
                {availableLgas.map((lga) => (
                  <option key={lga} value={lga}>
                    {lga} LGA
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* Confirmed Location Breakdown Card */}
      <div className="p-3.5 sm:p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-900">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>Verified Nigerian Location Match</span>
          </div>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
            {isLgaConfident ? 'High Confidence' : 'Review LGA'}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          <div className="p-2 rounded-xl bg-white border border-emerald-100">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block">State</span>
            <span className="font-bold text-slate-900">{selectedState}</span>
          </div>

          <div className="p-2 rounded-xl bg-white border border-emerald-100">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block">LGA</span>
            <div className="flex items-center justify-between">
              <span className="font-bold text-slate-900">{selectedLga} LGA</span>
              {!isLgaConfident && (
                <button
                  type="button"
                  onClick={() => setMode('manual')}
                  className="text-[10px] text-emerald-700 underline font-medium cursor-pointer"
                >
                  Change
                </button>
              )}
            </div>
          </div>

          <div className="p-2 rounded-xl bg-white border border-emerald-100">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block">Area / Hub</span>
            <span className="font-bold text-slate-900 truncate block">
              {cityArea || selectedLga || 'Metro'}
            </span>
          </div>
        </div>

        {/* GPS Coordinates & Privacy Notice */}
        <div className="flex items-center justify-between pt-1 text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            {latitude && longitude ? (
              <span>Coordinates Captured: {latitude.toFixed(4)}, {longitude.toFixed(4)}</span>
            ) : (
              <span>Using administrative boundary center</span>
            )}
          </span>
          <span className="text-[10px] text-slate-400 font-medium hidden sm:inline">
            🔒 Exact coordinates protected by NDPA 2023
          </span>
        </div>
      </div>
    </div>
  );
};
